import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import { Users, MessageCircle, Calendar, CreditCard, MapPin, User, Scan, QrCode, Mail, Globe, Plus, Eye } from 'lucide-react';
import { getUser, getAuthHeaders } from '../lib/auth';
import SidebarNavigation from '../components/SidebarNavigation';
import BusinessCardScanner from '../components/BusinessCardScanner';
import QRCodeScanner from '../components/QRCodeScanner';
import { toast } from 'sonner';
import axios from 'axios';
import AdSenseAd from '../components/AdSenseAd';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DigiConnect = () => {
  const user = getUser();
  const navigate = useNavigate();
  const [showScanner, setShowScanner] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [myConnections, setMyConnections] = useState([]);
  const [myWebsite, setMyWebsite] = useState(null);
  const [loadingWebsite, setLoadingWebsite] = useState(true);

  useEffect(() => {
    loadMyConnections();
    checkMyWebsite();
  }, []);

  const loadMyConnections = () => {
    try {
      const savedContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
      setMyConnections(savedContacts.slice(0, 6)); // Show first 6
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const checkMyWebsite = async () => {
    try {
      const response = await axios.get(`${API}/auto-websites/my-website`, {
        headers: getAuthHeaders()
      });
      if (response.data.website) {
        setMyWebsite(response.data.website);
      }
    } catch (error) {
      console.log('No website yet');
    } finally {
      setLoadingWebsite(false);
    }
  };

  const handleSaveContact = async (contactData) => {
    loadMyConnections(); // Refresh connections list
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <SidebarNavigation />
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        appName="DigiConnect"
        appIcon="🔗"
        appColor="#06b6d4"
        storageKey="digiconnect-install-prompt"
      />
      <PWAInstallPrompt />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Digi Connect
            </h1>
            <Link to="/contacts">
              <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full hover:shadow-lg transition-shadow flex items-center gap-2">
                <User className="w-4 h-4" />
                View My Contacts
              </button>
            </Link>
          </div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Your complete digital networking hub - Connect, share, and grow your professional network
          </p>
        </div>

        {/* AdSense Ad - Header */}
        <div className="mb-8">
          <AdSenseAd 
            slot="1234567890"
            format="auto"
            responsive={true}
            className="max-w-4xl mx-auto"
          />
        </div>

        {/* Website CTA Banner */}
        {!loadingWebsite && (
          <div className="mb-8">
            <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white overflow-hidden">
              <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl sm:text-2xl font-bold mb-1">
                      {myWebsite ? '✨ Your Website is Live!' : '🚀 Create Your Website'}
                    </h3>
                    <p className="text-emerald-50 text-sm sm:text-base">
                      {myWebsite 
                        ? 'Share your professional website with the world' 
                        : 'Build a stunning website from your business card in just 3 steps'}
                    </p>
                  </div>
                </div>
                <Link to={myWebsite ? `/auto-site/${myWebsite.id}` : '/auto-website'}>
                  <button className="bg-white text-emerald-600 px-6 py-3 rounded-full font-bold hover:shadow-xl transition-all flex items-center gap-2 whitespace-nowrap">
                    {myWebsite ? (
                      <>
                        <Eye className="w-5 h-5" />
                        View Website
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        Create Now
                      </>
                    )}
                  </button>
                </Link>
              </div>
            </Card>
          </div>
        )}

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="digi-connect-features">
          {/* My Digital Card */}
          <Link to={`/profile/${user?.id}`} data-testid="feature-my-card">
            <Card className="p-8 hover:shadow-xl transition-all cursor-pointer bg-white h-full group">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-5 bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl group-hover:scale-110 transition-transform">
                  <CreditCard className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    My Digital Card
                  </h3>
                  <p className="text-sm text-slate-600">
                    View & edit your digital business card with QR code and vCard download
                  </p>
                </div>
              </div>
            </Card>
          </Link>

          {/* Browse Users */}
          <Link to="/browse" data-testid="feature-browse">
            <Card className="p-8 hover:shadow-xl transition-all cursor-pointer bg-white h-full group">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-5 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl group-hover:scale-110 transition-transform">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    Browse Users
                  </h3>
                  <p className="text-sm text-slate-600">
                    Explore all users or discover professionals near you within 1 mile
                  </p>
                </div>
              </div>
            </Card>
          </Link>

          {/* Nearby Users */}
          <Link to="/browse?tab=nearby" data-testid="feature-nearby">
            <Card className="p-8 hover:shadow-xl transition-all cursor-pointer bg-white h-full group">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-5 bg-gradient-to-br from-green-600 to-green-800 rounded-2xl group-hover:scale-110 transition-transform">
                  <MapPin className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    Nearby Connect
                  </h3>
                  <p className="text-sm text-slate-600">
                    Find and connect with professionals and businesses within 1 mile
                  </p>
                </div>
              </div>
            </Card>
          </Link>

          {/* Business Card Scanner */}
          <div onClick={() => setShowScanner(true)} data-testid="feature-scanner">
            <Card className="p-8 hover:shadow-xl transition-all cursor-pointer bg-white h-full group">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-5 bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl group-hover:scale-110 transition-transform">
                  <Scan className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    Card Scanner
                  </h3>
                  <p className="text-sm text-slate-600">
                    Scan business cards and extract contact information
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* QR Code Scanner */}
          <div onClick={() => setShowQRScanner(true)} data-testid="feature-qr-scanner">
            <Card className="p-8 hover:shadow-xl transition-all cursor-pointer bg-white h-full group">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-5 bg-gradient-to-br from-pink-600 to-rose-600 rounded-2xl group-hover:scale-110 transition-transform">
                  <QrCode className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    QR Scanner
                  </h3>
                  <p className="text-sm text-slate-600">
                    Scan QR codes to instantly save contact information
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* My Connections */}
          <Link to="/contacts" data-testid="feature-connections">
            <Card className="p-8 hover:shadow-xl transition-all cursor-pointer bg-white h-full group">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-5 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-2xl group-hover:scale-110 transition-transform">
                  <Mail className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                    My Connections
                  </h3>
                  <p className="text-sm text-slate-600">
                    View all saved contacts ({myConnections.length})
                  </p>
                </div>
              </div>
            </Card>
          </Link>
        </div>

        {/* AdSense Ad - Mid Content */}
        <div className="mt-12 mb-8">
          <AdSenseAd 
            slot="2345678901"
            format="rectangle"
            responsive={true}
            className="max-w-4xl mx-auto"
          />
        </div>

        {/* My Connections Preview */}
        {myConnections.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                My Connections
              </h2>
              <Link to="/contacts" className="text-blue-600 hover:text-blue-700 font-semibold">
                View All ({JSON.parse(localStorage.getItem('scannedContacts') || '[]').length})
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myConnections.map((contact) => (
                <Card key={contact.id} className="p-4 hover:shadow-md transition-all bg-white">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{contact.name || 'No Name'}</h3>
                      <p className="text-sm text-slate-600 truncate">{contact.businessName || contact.title || 'No Company'}</p>
                      {contact.email && (
                        <p className="text-xs text-slate-500 truncate">{contact.email}</p>
                      )}
                      <span className="text-xs text-purple-600 mt-1 inline-block">
                        {contact.source === 'qr' ? '📱 QR Code' : '📇 Business Card'}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* AdSense Ad - Bottom */}
        <div className="mt-16 mb-8">
          <AdSenseAd 
            slot="3456789012"
            format="auto"
            responsive={true}
            className="max-w-4xl mx-auto"
          />
        </div>

        {/* Info Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <Card className="p-8 bg-white">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              About Digi Connect
            </h2>
            <div className="space-y-4 text-slate-700">
              <p>
                <strong>Digi Connect</strong> is your all-in-one digital networking platform that brings together 
                everything you need to build and maintain professional relationships.
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Digital Business Cards:</strong> Create and share professional digital cards with QR codes</li>
                <li><strong>Smart Discovery:</strong> Find users by browsing all members or discovering nearby professionals</li>
                <li><strong>Community Engagement:</strong> Share ideas, requirements, and engage with your network</li>
                <li><strong>Direct Communication:</strong> Chat with users and book appointments seamlessly</li>
                <li><strong>Profile Management:</strong> Maintain your digital presence with customizable profiles</li>
              </ul>
            </div>
          </Card>
        </div>
      </div>

      {/* Business Card Scanner Modal */}
      <BusinessCardScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onSave={handleSaveContact}
      />

      {/* QR Code Scanner Modal */}
      <QRCodeScanner
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onSave={handleSaveContact}
      />
    </div>
  );
};

export default DigiConnect;
